import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Activity, Users, Smartphone, Monitor, TrendingUp, Clock, BarChart3, Eye, X, ChevronRight } from "lucide-react";

const ROLES = [
    { id: "super_admin", label: "Admin", color: "bg-oxford" },
    { id: "secretary", label: "Sekretarë", color: "bg-amber-500" },
    { id: "quality_coordinator", label: "Koordinatorë", color: "bg-purple-500" },
    { id: "teacher", label: "Mësues", color: "bg-sage" },
    { id: "parent", label: "Prindër", color: "bg-terracotta" },
    { id: "student", label: "Nxënës", color: "bg-seablue" },
];

const formatDate = (iso) => iso ? new Date(iso).toLocaleString("sq-AL", { dateStyle: "short", timeStyle: "short" }) : "—";
const timeAgo = (iso) => {
    if (!iso) return "kurrë";
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return "tani";
    if (diff < 3600) return `${Math.floor(diff / 60)}m më parë`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h më parë`;
    if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d më parë`;
    return new Date(iso).toLocaleDateString("sq-AL");
};

function StatCard({ icon, label, value, sub, tone = "oxford" }) {
    const toneClass = {
        oxford: "border-oxford/30 bg-oxford/5",
        sage: "border-sage/40 bg-sage/5",
        amber: "border-amber-300 bg-amber-50",
        terracotta: "border-terracotta/30 bg-terracotta/5",
    }[tone];
    return (
        <div className={`bg-white border-2 ${toneClass} rounded-2xl p-4`}>
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-bold text-slate-600">{icon} {label}</div>
            <div className="font-heading text-3xl font-black text-slate-900 mt-2">{value}</div>
            {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
        </div>
    );
}

function LoginsBarChart({ series }) {
    const max = Math.max(1, ...series.map((s) => s.total));
    return (
        <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="logins-chart">
            <div className="flex items-center gap-2 mb-3">
                <BarChart3 className="w-4 h-4 text-oxford" />
                <h3 className="text-xs uppercase tracking-widest font-bold text-slate-700">Login-et për ditë</h3>
                <div className="ml-auto text-[11px] text-slate-500">{series.reduce((a, s) => a + s.total, 0)} gjithsej</div>
            </div>
            <div className="flex items-end gap-1 h-40">
                {series.map((s) => {
                    const h = max ? Math.max(2, (s.total / max) * 100) : 2;
                    return (
                        <div key={s.day} className="flex-1 flex flex-col items-center" title={`${s.day}: ${s.total}`}>
                            <div className="w-full bg-oxford rounded-t-md transition-all hover:bg-oxfordhover" style={{ height: `${h}%` }} />
                            <div className="text-[9px] text-slate-500 mt-1 font-mono">{s.day.slice(8)}</div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function HeatmapView({ grid, max }) {
    const days = ["E hënë", "E martë", "E mërkurë", "E enjte", "E premte", "E shtunë", "E diel"];
    return (
        <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="heatmap">
            <div className="flex items-center gap-2 mb-3">
                <Clock className="w-4 h-4 text-oxford" />
                <h3 className="text-xs uppercase tracking-widest font-bold text-slate-700">Aktiviteti sipas orës (30 ditë)</h3>
            </div>
            <div className="overflow-x-auto">
                <div className="grid grid-cols-[80px_repeat(24,_minmax(20px,_1fr))] gap-0.5 text-[9px]">
                    <div></div>
                    {Array.from({ length: 24 }).map((_, h) => (
                        <div key={`hdr-${h}`} className="text-center text-slate-400 font-mono">{h}</div>
                    ))}
                    {grid.map((row, i) => (
                        <div key={`row-${i}`} className="contents">
                            <div className="text-slate-600 font-bold flex items-center pr-2">{days[i]}</div>
                            {row.map((v, j) => {
                                const intensity = max ? v / max : 0;
                                const opacity = Math.max(0.05, intensity);
                                return (
                                    <div key={`c-${i}-${j}`} className="aspect-square rounded-sm" title={`${days[i]} ${j}:00 → ${v} kërkesa`}
                                        style={{ backgroundColor: v ? `rgba(16, 86, 139, ${opacity})` : "#f1f5f9" }} />
                                );
                            })}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

export default function AdminAnalytics() {
    const [overview, setOverview] = useState(null);
    const [users, setUsers] = useState([]);
    const [heatmap, setHeatmap] = useState(null);
    const [periodDays, setPeriodDays] = useState(7);
    const [search, setSearch] = useState("");
    const [roleFilter, setRoleFilter] = useState("");
    const [loading, setLoading] = useState(true);
    const [detail, setDetail] = useState(null);

    const refresh = async () => {
        setLoading(true);
        try {
            const [ov, us, hm] = await Promise.all([
                api.get("/admin/analytics/overview", { params: { days: periodDays } }),
                api.get("/admin/analytics/users", { params: { days: 30 } }),
                api.get("/admin/analytics/heatmap", { params: { days: 30 } }),
            ]);
            setOverview(ov.data);
            setUsers(us.data);
            setHeatmap(hm.data);
        } finally { setLoading(false); }
    };
    useEffect(() => { refresh(); }, [periodDays]);

    const filtered = useMemo(() => {
        let rows = users;
        if (roleFilter) rows = rows.filter((u) => u.role === roleFilter);
        if (search) {
            const q = search.toLowerCase();
            rows = rows.filter((u) => u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q));
        }
        return rows;
    }, [users, roleFilter, search]);

    const openDetail = async (uid) => {
        try {
            const r = await api.get(`/admin/analytics/user/${uid}`, { params: { days: 30 } });
            setDetail(r.data);
        } catch (e) { /* */ }
    };

    if (loading || !overview) {
        return <Layout><div className="text-slate-500">Duke ngarkuar analitikat…</div></Layout>;
    }

    const totalDevices = (overview.devices.mobile || 0) + (overview.devices.desktop || 0) || 1;
    const mobilePct = Math.round(100 * overview.devices.mobile / totalDevices);

    return (
        <Layout>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-6">
                <div>
                    <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-2 flex items-center gap-2">
                        <Activity className="w-3 h-3" /> Përdorimi i platformës
                    </div>
                    <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-black text-slate-900 tracking-tight">Analitikat</h1>
                    <p className="text-slate-600 mt-2 text-sm">Sa po e përdorin platformën mësuesit, prindërit dhe nxënësit — në kohë reale.</p>
                </div>
                <div className="flex items-center gap-2">
                    <select value={periodDays} onChange={(e) => setPeriodDays(parseInt(e.target.value))} className="h-10 rounded-md border border-slate-300 bg-white text-sm px-3 font-bold" data-testid="period-select">
                        <option value={7}>7 ditë</option>
                        <option value={14}>14 ditë</option>
                        <option value={30}>30 ditë</option>
                        <option value={90}>90 ditë</option>
                    </select>
                </div>
            </div>

            {/* Top KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
                <StatCard icon={<TrendingUp className="w-3 h-3" />} label="Aktivë sot" value={overview.active.today} sub="Të dalluar" tone="sage" />
                <StatCard icon={<Users className="w-3 h-3" />} label="Aktivë (7 ditë)" value={overview.active.week} tone="oxford" />
                <StatCard icon={<Users className="w-3 h-3" />} label="Aktivë (30 ditë)" value={overview.active.month} tone="oxford" />
                <StatCard icon={<Smartphone className="w-3 h-3" />} label="Mobile" value={`${mobilePct}%`} sub={`${overview.devices.mobile} kërkesa`} tone="amber" />
            </div>

            {/* Active by Role */}
            <div className="bg-white border border-slate-200 rounded-2xl p-5 mb-5">
                <div className="flex items-center gap-2 mb-4">
                    <Users className="w-4 h-4 text-oxford" />
                    <h3 className="text-xs uppercase tracking-widest font-bold text-slate-700">Aktivë sipas rolit</h3>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                    {ROLES.map((r) => {
                        const stats = overview.active_by_role[r.id] || { today: 0, week: 0, month: 0, registered: 0 };
                        const pct = stats.registered ? Math.round(100 * stats.month / stats.registered) : 0;
                        return (
                            <div key={r.id} className="border border-slate-200 rounded-xl p-3" data-testid={`role-stats-${r.id}`}>
                                <div className="flex items-center gap-2 mb-2">
                                    <div className={`w-2 h-2 rounded-full ${r.color}`} />
                                    <div className="text-[10px] uppercase font-bold text-slate-600">{r.label}</div>
                                </div>
                                <div className="font-heading text-2xl font-black text-slate-900">{stats.month}<span className="text-sm text-slate-400">/{stats.registered}</span></div>
                                <div className="text-[10px] text-slate-500 mt-0.5">{pct}% të rolit</div>
                                <div className="flex justify-between text-[10px] text-slate-500 mt-2 pt-2 border-t border-slate-100">
                                    <span>Sot: <strong className="text-slate-700">{stats.today}</strong></span>
                                    <span>7d: <strong className="text-slate-700">{stats.week}</strong></span>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
                <LoginsBarChart series={overview.logins_per_day} />
                {heatmap && <HeatmapView grid={heatmap.grid} max={heatmap.max} />}
            </div>

            {/* Top paths + events */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
                <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
                    <div className="px-5 py-3 border-b border-slate-200 bg-bone/40">
                        <div className="text-xs uppercase tracking-widest font-bold text-slate-700">Faqet më të vizituara</div>
                    </div>
                    <div className="divide-y divide-slate-200">
                        {overview.top_paths.map((p) => (
                            <div key={p.path} className="px-5 py-2.5 flex items-center gap-3" data-testid={`top-path-${p.path}`}>
                                <code className="text-xs text-slate-700 truncate flex-1 font-mono">{p.path}</code>
                                <span className="text-[11px] text-slate-500">{p.uniq_users} përd.</span>
                                <span className="font-mono font-bold text-sm text-oxford">{p.hits.toLocaleString()}</span>
                            </div>
                        ))}
                    </div>
                </div>
                <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
                    <div className="px-5 py-3 border-b border-slate-200 bg-bone/40">
                        <div className="text-xs uppercase tracking-widest font-bold text-slate-700">Veprime kryesore (events)</div>
                    </div>
                    {overview.top_events.length === 0 ? (
                        <div className="p-8 text-center text-sm text-slate-500">Asnjë event i regjistruar</div>
                    ) : (
                        <div className="divide-y divide-slate-200">
                            {overview.top_events.map((e) => (
                                <div key={e.name} className="px-5 py-2.5 flex items-center gap-3">
                                    <span className="text-sm font-semibold text-slate-700 truncate flex-1">{e.name}</span>
                                    <span className="font-mono font-bold text-sm text-sage">{e.count.toLocaleString()}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Users table */}
            <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden mb-8">
                <div className="px-5 py-3 border-b border-slate-200 bg-bone/40 flex items-center gap-3">
                    <Users className="w-4 h-4 text-oxford" />
                    <div className="text-xs uppercase tracking-widest font-bold text-slate-700">Përdoruesit ({filtered.length}/{users.length})</div>
                    <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Kërko..." className="ml-auto h-9 rounded-md border border-slate-300 bg-white text-sm px-3 w-44" data-testid="users-search" />
                    <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} className="h-9 rounded-md border border-slate-300 bg-white text-sm px-2" data-testid="users-role-filter">
                        <option value="">Të gjitha rolet</option>
                        {ROLES.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
                    </select>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-bone/30">
                            <tr className="text-[10px] uppercase tracking-widest text-slate-600 text-left">
                                <th className="px-4 py-2 font-bold">Emri</th>
                                <th className="px-4 py-2 font-bold">Roli</th>
                                <th className="px-4 py-2 font-bold text-right">Login</th>
                                <th className="px-4 py-2 font-bold text-right">Kërkesa</th>
                                <th className="px-4 py-2 font-bold text-right">Min aktiv</th>
                                <th className="px-4 py-2 font-bold">I parë i fundit</th>
                                <th className="px-4 py-2 font-bold w-8"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.length === 0 ? (
                                <tr><td colSpan={7} className="text-center py-8 text-slate-500 text-sm">Asnjë përdorues</td></tr>
                            ) : filtered.map((u) => {
                                const role = ROLES.find((r) => r.id === u.role);
                                const inactive = !u.last_seen;
                                return (
                                    <tr key={u.id} className="border-t border-slate-100 hover:bg-bone/30 cursor-pointer" onClick={() => openDetail(u.id)} data-testid={`user-row-${u.id}`}>
                                        <td className="px-4 py-2.5">
                                            <div className="font-bold text-slate-900 text-sm">{u.name}</div>
                                            <div className="text-[10px] text-slate-500">{u.email}</div>
                                        </td>
                                        <td className="px-4 py-2.5">
                                            <span className="inline-flex items-center gap-1.5 text-[11px]">
                                                <div className={`w-2 h-2 rounded-full ${role?.color || "bg-slate-400"}`} />
                                                {role?.label || u.role}
                                            </span>
                                        </td>
                                        <td className="px-4 py-2.5 text-right font-mono">{u.logins}</td>
                                        <td className="px-4 py-2.5 text-right font-mono">{u.request_count.toLocaleString()}</td>
                                        <td className="px-4 py-2.5 text-right font-mono">{u.active_minutes}</td>
                                        <td className={`px-4 py-2.5 text-xs ${inactive ? "text-slate-400 italic" : "text-slate-700"}`}>
                                            {timeAgo(u.last_seen)}
                                        </td>
                                        <td className="px-4 py-2.5 text-right">
                                            <ChevronRight className="w-4 h-4 text-slate-400 inline" />
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* User detail modal */}
            {detail && (
                <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-start sm:items-center justify-center p-4 overflow-y-auto" onClick={() => setDetail(null)}>
                    <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl max-w-3xl w-full max-h-[92vh] overflow-y-auto my-8" data-testid="user-detail">
                        <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-3 sticky top-0 bg-white z-10">
                            <div className="w-12 h-12 rounded-xl bg-oxford/10 text-oxford flex items-center justify-center font-bold shrink-0">
                                {detail.user.name.split(" ").map((n) => n[0]).slice(0, 2).join("")}
                            </div>
                            <div className="flex-1 min-w-0">
                                <h3 className="font-heading text-xl font-bold text-slate-900 truncate">{detail.user.name}</h3>
                                <div className="text-xs text-slate-500 truncate">{detail.user.email} • {detail.user.role}</div>
                            </div>
                            <button onClick={() => setDetail(null)} className="p-2 rounded-md hover:bg-slate-100">
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                        <div className="p-5 space-y-5">
                            <div className="grid grid-cols-3 gap-3">
                                <div className="border border-slate-200 rounded-xl p-3">
                                    <div className="text-[10px] uppercase font-bold text-slate-500">Min aktiv (30d)</div>
                                    <div className="font-heading text-2xl font-black text-oxford">{detail.total_active_minutes}</div>
                                </div>
                                <div className="border border-slate-200 rounded-xl p-3">
                                    <div className="text-[10px] uppercase font-bold text-slate-500">Sesione</div>
                                    <div className="font-heading text-2xl font-black text-sage">{detail.sessions.length}</div>
                                </div>
                                <div className="border border-slate-200 rounded-xl p-3">
                                    <div className="text-[10px] uppercase font-bold text-slate-500">Veprime</div>
                                    <div className="font-heading text-2xl font-black text-amber-700">{detail.activity.length}</div>
                                </div>
                            </div>
                            <div>
                                <div className="text-xs uppercase tracking-widest font-bold text-slate-700 mb-2">Faqet kryesore</div>
                                {detail.top_paths.length === 0 ? (
                                    <div className="text-xs text-slate-500">Pa aktivitet</div>
                                ) : (
                                    <div className="space-y-1">
                                        {detail.top_paths.slice(0, 8).map((p) => (
                                            <div key={p.path} className="flex items-center gap-2 text-xs">
                                                <code className="flex-1 truncate text-slate-700 font-mono">{p.path}</code>
                                                <span className="font-mono font-bold text-oxford">{p.hits}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <div>
                                <div className="text-xs uppercase tracking-widest font-bold text-slate-700 mb-2">Aktiviteti i fundit</div>
                                <div className="max-h-64 overflow-y-auto border border-slate-200 rounded-md divide-y divide-slate-100">
                                    {detail.activity.slice(0, 30).map((a, i) => (
                                        <div key={i} className="px-3 py-1.5 flex items-center gap-2 text-[11px]">
                                            <span className="text-slate-400 font-mono w-32 shrink-0">{formatDate(a.at)}</span>
                                            <span className={`font-mono w-12 shrink-0 ${a.status >= 400 ? "text-red-600" : "text-slate-500"}`}>{a.method}</span>
                                            <code className="flex-1 truncate text-slate-700">{a.path}</code>
                                            <span className={`font-mono font-bold ${a.status >= 400 ? "text-red-600" : "text-sage"}`}>{a.status}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </Layout>
    );
}
